import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

export const useChatQueries = () => {
  const api = useApi();
  const projectId = useProjectId();

  return {
    chatDetails: ({ chatId }: { chatId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.CHATS_DETAILS(projectId, chatId),
        queryFn: () => api.getChatsDetailsV2(chatId),
        select: commonAPIDataSelector,
      }),
    privateChatDetails: ({ userId }: { userId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.USER_CHAT_DETAILS(projectId, userId),
        queryFn: () => api.getChatsUserDetailsV2(userId),
        select: commonAPIDataSelector,
      }),
    listChatRepliesInfinite: ({ chatId }: { chatId: string }) => {
      const limit = 10;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.CHATS_REPLIES(projectId, chatId),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getChatsRepliesV2(chatId, { Offset: pageParam * limit, Limit: limit })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 1000 * 60 * 5, // 5 minutes
      });
    },
    chatMembers: ({ chatId }: { chatId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.CHATS_MEMBERS(projectId, chatId),
        queryFn: () => api.getChatsMembersV2(chatId),
        select: commonAPIDataSelector,
      }),
    listInfinite: (query?: ApiQueryParams<"getChatsV2">) => {
      const limit = 50;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.CHATS_LIST(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getChatsV2({ ...query, Offset: pageParam * limit, Limit: limit })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    listAvailableUsersInfinite: (search?: string) => {
      const limit = 20;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.CHATS_AVAILABLE_USERS(projectId, search || ""),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getChatsAvailableUsersV1({
              search,
              Offset: pageParam * limit,
              Limit: limit,
            })
            .then(commonAPIDataSelector),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
        staleTime: 30 * 1000, // 30 seconds
      });
    },
  };
};
